import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2 } from 'lucide-react';
import {
  FaLeaf,
  FaMedal ,
  FaPhoneAlt,
  FaBriefcase,
  FaBuilding,
  FaDollarSign
} from 'react-icons/fa';

import clientAxios from '../api/clientAxios';

const Chatbox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! Soy el asistente virtual de Campo Fresco. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const messagesEndRef = useRef(null);

  // Opciones rápidas mejoradas

const quickOptions = [
  {
    id: 'productos',
    icon: <FaLeaf className="text-green-600 w-4 h-4 mr-2" />,
    text: 'Ver Productos',
    message: 'Me interesa conocer sus productos'
  },
  {
  id: 'calidad',
  icon: <FaMedal className="text-gray-700	 w-4 h-4 mr-2" />,
  text: 'Certificaciones',
  message: 'Quiero saber sobre su calidad y certificaciones'
  },
  {
    id: 'contacto',
    icon: <FaPhoneAlt className="text-orange-500 w-4 h-4 mr-2" />,
    text: 'Contacto',
    message: 'Necesito información de contacto'
  },
  {
    id: 'trabajo',
    icon: <FaBriefcase className="text-amber-600 w-4 h-4 mr-2" />,
    text: 'Trabajar aquí',
    message: 'Me interesa trabajar en Campo Fresco'
  },
  {
    id: 'nosotros',
    icon: <FaBuilding className="text-blue-500 w-4 h-4 mr-2" />,
    text: 'Sobre nosotros',
    message: 'Quiero conocer más sobre Campo Fresco'
  },
  {
    id: 'precios',
    icon: <FaDollarSign className="text-emerald-500 w-4 h-4 mr-2" />,
    text: 'Precios',
    message: 'Necesito información sobre precios'
  }
];



  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { checkConnection(); }, []);

  // Verificar conexión al backend
  const checkConnection = async () => {
   setConnectionStatus('connected');
  };

  // Llama al backend para obtener respuesta del bot
  const callAPI = async (userMessage) => {
    try {
      const res = await clientAxios.post('/chat', { message: userMessage });
      return res.data && res.data.bot ? res.data.bot : 'Lo siento, ha ocurrido un error técnico. Por favor intenta más tarde o contacta directamente a nuestro equipo en contacto@campofresco.com.mx';
    } catch {
      return 'Lo siento, ha ocurrido un error técnico. Por favor intenta más tarde o contacta directamente a nuestro equipo en contacto@campofresco.com.mx';
    }
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === '') return;
    const newMessage = { id: Date.now(), text: inputMessage, sender: 'user', timestamp: new Date() };
    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsTyping(true);
    setShowQuickOptions(false);
    try {
      const botResponseText = await callAPI(newMessage.text);
      setMessages(prev => [...prev, { id: Date.now() + 1, text: botResponseText, sender: 'bot', timestamp: new Date() }]);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "Disculpa, ha ocurrido un error. Por favor intenta nuevamente o contacta a contacto@campofresco.com.mx 📧", sender: 'bot', timestamp: new Date() }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickOption = async (option) => {
    const newMessage = { id: Date.now(), text: option.message, sender: 'user', timestamp: new Date() };
    setMessages(prev => [...prev, newMessage]);
    setIsTyping(true);
    setShowQuickOptions(false);
    try {
      const botResponseText = await callAPI(option.message);
      setMessages(prev => [...prev, { id: Date.now() + 1, text: botResponseText, sender: 'bot', timestamp: new Date() }]);
    } catch {
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "Disculpa, ha ocurrido un error. Por favor intenta nuevamente.", sender: 'bot', timestamp: new Date() }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => timestamp.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

  // Convierte texto markdown a HTML simple
  function markdownToHtml(markdown) {
    let html = markdown;
    html = html.replace(/^###### (.*)$/gm, '<h6>$1</h6>');
    html = html.replace(/^##### (.*)$/gm, '<h5>$1</h5>');
    html = html.replace(/^#### (.*)$/gm, '<h4>$1</h4>');
    html = html.replace(/^### (.*)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*)$/gm, '<h1>$1</h1>');
    html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<b><i>$1</i></b>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
    html = html.replace(/\*(.*?)\*/g, '<i>$1</i>');
    html = html.replace(/^\d+\. (.*)$/gm, '<li>$1</li>');
    html = html.replace(/^[-•] (.*)$/gm, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>\s*)+/g, match => `<ul>${match}</ul>`);
    html = html.replace(/\n/g, '<br>');
    html = html.replace(/---+/g, '<hr>');
    return html;
  }

  return (
    <>
      {/* Chat Button con indicador de estado */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
              isOpen 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-green-600 hover:bg-green-700 hover:scale-110'
            }`}
          >
            {isOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <MessageCircle className="w-6 h-6 text-white" />
            )}
          </button>
          
          {/* Indicador de estado de conexión */}
          <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
            connectionStatus === 'connected' ? 'bg-green-400' :
            connectionStatus === 'error' ? 'bg-red-400' :
            connectionStatus === 'checking' ? 'bg-yellow-400 animate-pulse' :
            'bg-gray-400'
          }`}></div>
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[32rem] bg-white rounded-lg shadow-2xl border border-gray-200 z-50 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <Bot className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Freskin</h3>
                  <p className="text-xs text-green-100 flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-1 ${
                      connectionStatus === 'connected' ? 'bg-green-300' :
                      connectionStatus === 'error' ? 'bg-red-300' :
                      'bg-gray-300'
                    }`}></span>
                    {connectionStatus === 'connected' ? 'En línea' :
                     connectionStatus === 'error' ? 'Error de conexión' :
                     connectionStatus === 'checking' ? 'Conectando...' :
                     'Sin configurar'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
              >
                <div className={`flex items-start space-x-2 max-w-xs ${
                  message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                    message.sender === 'user' ? 'bg-blue-500' : 'bg-green-500'
                  }`}>
                    {message.sender === 'user' ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div>
                    <div className={`p-3 rounded-lg shadow-sm ${
                      message.sender === 'user' 
                        ? 'bg-blue-500 text-white rounded-br-none' 
                        : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
                    }`}>
                      {message.sender === 'bot' ? (
                        <span className="text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: markdownToHtml(message.text) }} />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                      )}
                      <p className={`text-xs mt-1 ${
                        message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start animate-fade-in">
                <div className="flex items-start space-x-2">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-white p-3 rounded-lg rounded-bl-none shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                      <span className="text-sm text-gray-600">Freskin está escribiendo...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Options */}
            {showQuickOptions && !isTyping && (
              <div className="space-y-2 animate-fade-in">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-2"><br />Elige alguna opción o escribe tu mensaje...</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {quickOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleQuickOption(option)}
                      className="p-3 text-xs bg-white hover:bg-green-50 text-gray-700 rounded-lg border border-gray-200 hover:border-green-300 transition-all duration-200 text-left shadow-sm hover:shadow"
                    >

                      <div className="flex items-center font-medium">
                        {option.icon}
                        {option.text}
                      </div>

                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white">
            {!showQuickOptions && messages.length > 2 && (
              <div className="mb-2">
                <button
                  onClick={() => setShowQuickOptions(true)}
                  className="text-xs text-green-600 hover:text-green-700 underline"
                >
                  Ver opciones rápidas
                </button>
              </div>
            )}
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isTyping ? "Esperando respuesta..." : "Escribe tu mensaje..."}
                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                disabled={isTyping || connectionStatus !== 'connected'}
              />
              <button
                onClick={handleSendMessage}
                disabled={isTyping || !inputMessage.trim() || connectionStatus !== 'connected'}
                className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTyping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
            
            {connectionStatus !== 'connected' && (
              <p className="text-xs text-red-600 mt-2">
                {connectionStatus === 'no-key' ? 'Error de configuración de API' :
                 connectionStatus === 'error' ? 'Error de conexión con OpenAI' :
                 'Conectando...'}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbox;